// src/utils/firebasePhoneAuth.js
// Import the core firebase app and the auth service as they were typically imported in Firebase v8
import firebase from 'firebase/app';
import 'firebase/auth'; // Import auth service for side effects

let authInstance; // Renamed to avoid confusion with the global 'auth' variable in old contexts
let recaptchaVerifierInstance; // Store the reCAPTCHA instance

/**
 * Initializes Firebase Auth and RecaptchaVerifier for a specific Firebase app's auth service.
 * This should be called once, typically on app startup or before the first OTP request.
 * @param {object} authService The Firebase Auth service instance (e.g., firebaseServices.customerProfile.auth).
 * @param {HTMLElement | string} recaptchaContainerId The ID or DOM element where reCAPTCHA will render.
 */
export function initializeFirebaseAuth(authService, recaptchaContainerId) {
    if (!authService) {
        throw new Error('Firebase Auth service instance is required for initialization.');
    }
    authInstance = authService; // Assign the provided auth service instance

    if (!recaptchaVerifierInstance) {
        // For Firebase v8, RecaptchaVerifier is directly on firebase.auth
        recaptchaVerifierInstance = new firebase.auth.RecaptchaVerifier(recaptchaContainerId, {
            'size': 'invisible', // or 'normal' if you want a visible widget
            'callback': (response) => {
                // reCAPTCHA solved, allows signInWithPhoneNumber to proceed.
                console.log('reCAPTCHA solved!');
            },
            'expired-callback': () => {
                console.error('reCAPTCHA expired. User should try again.');
            }
        });

        recaptchaVerifierInstance.render(); // Render the reCAPTCHA widget (even if invisible)
        console.log('Firebase Auth and reCAPTCHA initialized.');
    }
}

/**
 * Sends an OTP to the given phone number using Firebase Authentication.
 * Requires initializeFirebaseAuth to be called first with the correct auth service.
 * @param {string} phoneNumber The phone number in E.164 format (e.g., +66123456789).
 * @returns {Promise<firebase.auth.ConfirmationResult>} A promise that resolves with the ConfirmationResult object.
 * @throws {Error} Throws an error if authentication fails (e.g., invalid phone number, reCAPTCHA failure).
 */
export async function sendOtp(phoneNumber) {
    if (!authInstance || !recaptchaVerifierInstance) {
        throw new Error('Firebase Auth or reCAPTCHA not initialized. Call initializeFirebaseAuth first.');
    }
    if (!phoneNumber) {
        throw new Error('Phone number is required.');
    }

    try {
        // For Firebase v8, signInWithPhoneNumber is directly on the auth instance
        const confirmationResult = await authInstance.signInWithPhoneNumber(phoneNumber, recaptchaVerifierInstance);
        return confirmationResult;
    } catch (error) {
        console.error('Error sending OTP:', error.code, error.message);
        let errorMessage;
        switch (error.code) {
            case 'auth/invalid-phone-number':
                errorMessage = 'The phone number is invalid. Please check the format (e.g., +66XXXXXXXXX).';
                break;
            case 'auth/missing-phone-number':
                errorMessage = 'Please enter a phone number.';
                break;
            case 'auth/quota-exceeded':
                errorMessage = 'SMS quota exceeded for this project or number. Please try again later.';
                break;
            case 'auth/captcha-check-failed':
                errorMessage = 'reCAPTCHA verification failed. Please try again.';
                break;
            case 'auth/web-storage-unsupported':
                errorMessage = 'Your browser does not support web storage (cookies). Please enable cookies or try a different browser.';
                break;
            default:
                errorMessage = `Failed to send OTP: ${error.message}`;
        }
        throw new Error(errorMessage); // Re-throw with a user-friendly message
    }
}

/**
 * Verifies the OTP received by the user.
 * @param {firebase.auth.ConfirmationResult} confirmationResult The ConfirmationResult object obtained from sendOtp.
 * @param {string} verificationCode The 6-digit (or other length) code entered by the user.
 * @returns {Promise<firebase.auth.UserCredential>} A promise that resolves with the UserCredential object on success.
 * @throws {Error} Throws an error if OTP verification fails (e.g., invalid code, expired code).
 */
export async function verifyOtp(confirmationResult, verificationCode) {
    if (!confirmationResult) {
        throw new Error('No OTP sent. Call sendOtp first.');
    }
    if (!verificationCode) {
        throw new Error('Verification code is required.');
    }

    try {
        const userCredential = await confirmationResult.confirm(verificationCode);
        return userCredential;
    } catch (error) {
        console.error('Error verifying OTP:', error.code, error.message);
        let errorMessage;
        switch (error.code) {
            case 'auth/invalid-verification-code':
                errorMessage = 'Invalid verification code. Please try again.';
                break;
            case 'auth/code-expired':
                errorMessage = 'The verification code has expired. Please request a new one.';
                break;
            case 'auth/credential-already-in-use': // If the phone number is linked to another account during a re-auth scenario
                errorMessage = 'This phone number is already linked to another account.';
                break;
            default:
                errorMessage = `Failed to verify OTP: ${error.message}`;
        }
        throw new Error(errorMessage); // Re-throw with a user-friendly message
    }
}

/**
 * Resets the reCAPTCHA for the next verification attempt.
 * Call this if the reCAPTCHA expires or if you want to explicitly reset it for a new flow.
 */
export function resetRecaptcha() {
    if (recaptchaVerifierInstance) {
        recaptchaVerifierInstance.clear(); // Clear the reCAPTCHA state
        console.log('reCAPTCHA cleared.');
    }
}
